import { useEffect, useState } from 'react';
import { fetchClusterReport, ClusterReport } from '../../lib/api';
import { REPORT } from '../print/PrintAtoms';
import { getSiteDossier } from './keipDossier';
import {
  DossierMasthead, DossierHero, HeroFactsDeal, Designations,
  DataRoom, DossierFooter,
  SectionHead, Eyebrow, SANS, SERIF, MONO,
} from './DossierSections';
import { ClusterHero } from './ClusterHero';
import { ClusterMap } from './ClusterMap';

const PAGE: React.CSSProperties = {
  minHeight: '100vh', background: REPORT.white, color: REPORT.body,
  fontFamily: SANS, padding: '18px 24px 64px',
};
const SHELL: React.CSSProperties = { maxWidth: 1180, margin: '0 auto' };

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ ...PAGE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: REPORT.tertiary }}>{children}</div>
    </div>
  );
}

export function SiteClusterReport({ slug }: { slug: string }) {
  const [data, setData] = useState<ClusterReport | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => { fetchClusterReport(slug).then(setData).catch((e) => setErr(String(e))); }, [slug]);

  if (err) return <Centered>Failed to load cluster · {err}</Centered>;
  if (!data) return <Centered>Loading dossier…</Centered>;

  const dossier = getSiteDossier(slug);

  return (
    <div style={PAGE}>
      <div style={SHELL}>
        {dossier ? (
          <>
            <DossierMasthead d={dossier} slug={slug} />
            <DossierHero d={dossier} />
            <HeroFactsDeal d={dossier} />

            {/* Map sits high — the interconnection picture is the point of a
                grid-connected site, so it leads before the fact sheet. It
                carries its own "Interconnection map" header, so no section head
                here. The per-tract screening lives in its sidebar. */}
            <section style={{ marginTop: 40 }}>
              <ClusterMap report={data} selectedId={selectedId} onSelect={setSelectedId} occupants={dossier?.occupants} siteData={dossier?.factSheet} tractOverrides={dossier?.tractOverrides} flatOverlayUrl={dossier?.flatOverlayUrl} mouTracts={dossier?.deal.tracts.map((t) => t.id)} />
              <div style={{ marginTop: 12, fontFamily: SANS, fontSize: 11, lineHeight: 1.5, color: REPORT.tertiary, fontStyle: 'italic' }}>
                {dossier.analysisNote}
              </div>
            </section>

            <Designations d={dossier} />

            <DataRoom d={dossier} />
            <DossierFooter d={dossier} />
          </>
        ) : (
          // Generic (non-dossier) cluster fallback — editorial header + analysis only.
          <>
            <div style={{ marginTop: 8 }}>
              <Eyebrow>Site cluster · {data.cluster.county}, {data.cluster.state}</Eyebrow>
              <h1 style={{ margin: '8px 0 0', fontFamily: SERIF, fontWeight: 400, fontSize: 34, color: REPORT.heading, letterSpacing: '-0.015em' }}>{data.cluster.name}</h1>
              <div style={{ fontFamily: SANS, fontSize: 13, color: REPORT.secondary, marginTop: 6 }}>{data.tracts.length} tracts</div>
            </div>
            <section style={{ marginTop: 32 }}>
              <ClusterMap report={data} selectedId={selectedId} onSelect={setSelectedId} occupants={dossier?.occupants} siteData={dossier?.factSheet} tractOverrides={dossier?.tractOverrides} flatOverlayUrl={dossier?.flatOverlayUrl} mouTracts={dossier?.deal.tracts.map((t) => t.id)} />
            </section>
            <AnalysisSection data={data} />
          </>
        )}
      </div>
    </div>
  );
}

function AnalysisSection({ data }: { data: ClusterReport }) {
  return (
    <section style={{ marginTop: 56 }}>
      <SectionHead
        eyebrow="Atwater Analysis"
        title="Grid-power scoring"
        aside={data.site?.dataConfidence ? `${data.site.dataConfidence} data` : undefined}
      />
      <ClusterHero site={data.site} assemblies={data.assemblies} />
    </section>
  );
}
